import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Plus, Search, DollarSign, Trash2, CreditCard, Loader2, CheckSquare, Square, Mail, Layers, AlertTriangle, FileText, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import AddInvoiceModal from './AddInvoiceModal';
import BatchInvoiceModal from './BatchInvoiceModal';
import EnterPaymentModal from './EnterPaymentModal';
import EmailInvoiceModal from './EmailInvoiceModal';
import LoadDetailModal from './LoadDetailModal';
import { invoiceAgingDays, agingBucket, computeLoadGross, nextInvoiceNumber } from '@/lib/invoiceHelpers';

// Darker text shade for light mode, brighter for dark -- text-amber-400/
// text-green-400 alone were tuned for a dark background only and read as
// a near-invisible pale pill on a light background.
const STATUS_COLORS = {
  Unpaid: 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  Partial: 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Paid: 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
  Void: 'text-muted-foreground bg-muted/40 border-border',
};

// green (0-30) / yellow (31-60) / red (61-90+) -- days-since-invoiced.
// Used for the aging quick-filters only; Balance Due itself is plain
// text like every other amount column (no per-row color coding).
const BUCKET_FILTERS = [
  { key: 'green', label: '0-30 Days' },
  { key: 'yellow', label: '31-60 Days' },
  { key: 'red', label: '61-90+ Days' },
];

export default function BrokerInvoicesTab({ prefillLoadId, onPrefillConsumed, onInvoiceCreated, onInvoiceDeleted }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [batchPaying, setBatchPaying] = useState(false);
  const [emailingInvoice, setEmailingInvoice] = useState(null);
  const [remindingInvoice, setRemindingInvoice] = useState(null);
  const [viewLoad, setViewLoad] = useState(null);
  const [openingLoadId, setOpeningLoadId] = useState(null);

  // Load # opens the load's own detail view (rate con, BOL, receipts, fees).
  const openLoad = async (inv) => {
    setOpeningLoadId(inv.id);
    try {
      let rows = inv.load_id ? await client.entities.Load.filter({ id: inv.load_id }) : [];
      if (!rows[0] && inv.load_number) rows = await client.entities.Load.filter({ load_number: inv.load_number });
      if (rows[0]) setViewLoad(rows[0]);
      else toast.error('Could not find the load for this invoice.');
    } catch {
      toast.error('Could not open the load.');
    } finally {
      setOpeningLoadId(null);
    }
  };
  // Loads dispatch has clicked "Release to Accounting" on (status flipped
  // Delivered -> Invoiced) that never actually got an Invoice row -- that
  // status flip alone doesn't create one. Without surfacing these
  // separately, a released load just silently vanishes from view: it's
  // no longer 'Delivered' (so it drops off AdminPanel's Loads-tab invoice
  // actions) but nothing shows up here either.
  const [needsInvoiceLoads, setNeedsInvoiceLoads] = useState([]);
  const [needsInvoiceLoading, setNeedsInvoiceLoading] = useState(true);
  const [creatingInvoiceFor, setCreatingInvoiceFor] = useState(null); // load id, or 'bulk'
  // Click a column header to sort by it; click again to flip direction.
  // Defaults to newest-created first -- invoiced_date alone (the old
  // list() sort) ties constantly since most invoices for a given day
  // share the same date, so same-day invoices came back in whatever
  // order Supabase happened to return them, not actually newest-first.
  const [sortState, setSortState] = useState({ key: 'created_date', dir: -1 });

  // "Create Invoice" on a delivered load (AdminPanel's InvoiceDropdown)
  // lands here with a load already picked out -- open straight into Add
  // Invoice instead of dropping the dispatcher on an empty/unrelated list.
  useEffect(() => {
    if (prefillLoadId) setShowAdd(true);
  }, [prefillLoadId]);

  const loadInvoices = async () => {
    setLoading(true);
    // created_date is a real timestamp (unlike invoiced_date, a plain
    // date with no time component) -- always unique/monotonic, so this
    // is a reliable newest-first fetch order regardless of the
    // frontend sort state below, which re-sorts the result anyway.
    const data = await client.entities.Invoice.list('-created_date', 200);
    setInvoices(data);
    setLoading(false);
  };

  const loadNeedsInvoice = async () => {
    setNeedsInvoiceLoading(true);
    try {
      const [allLoads, allInvoices] = await Promise.all([
        client.entities.Load.list('-created_date', 500),
        client.entities.Invoice.list('-created_date', 1000),
      ]);
      const invoicedLoadIds = new Set(allInvoices.map(i => i.load_id).filter(Boolean));
      setNeedsInvoiceLoads(allLoads.filter(l => l.status === 'Invoiced' && !invoicedLoadIds.has(l.id)));
    } finally {
      setNeedsInvoiceLoading(false);
    }
  };

  useEffect(() => { loadInvoices(); loadNeedsInvoice(); }, []);

  const handleCreateInvoiceForLoad = async (load) => {
    setCreatingInvoiceFor(load.id);
    try {
      const allInvoices = await client.entities.Invoice.list('-created_date', 1000);
      const amount = computeLoadGross(load);
      await client.entities.Invoice.create({
        invoice_number: nextInvoiceNumber(allInvoices),
        load_id: load.id,
        load_number: load.load_number,
        customer_name: load.customer_name || 'Unknown',
        customer_email: load.customer_email || undefined,
        total_amount: amount,
        balance_due: amount,
        invoiced_date: new Date().toISOString().split('T')[0],
        status: 'Unpaid',
      });
      toast.success(`Invoice created for Load #${load.load_number}.`);
      loadInvoices();
      loadNeedsInvoice();
    } catch (e) {
      toast.error(`Could not create invoice for Load #${load.load_number}: ${e?.message || 'Unknown error'}`);
    } finally {
      setCreatingInvoiceFor(null);
    }
  };

  // Sequential, not Promise.all -- these are real financial-data writes and
  // a partial failure needs to be attributable to a specific load, not
  // lost in a batch rejection (same reasoning as the CSV importer).
  const handleCreateAllNeedsInvoice = async () => {
    if (needsInvoiceLoads.length === 0) return;
    if (!window.confirm(`Create ${needsInvoiceLoads.length} invoice(s), one per released load?`)) return;
    setCreatingInvoiceFor('bulk');
    let invoicesSoFar = await client.entities.Invoice.list('-created_date', 1000).catch(() => []);
    let success = 0;
    const errors = [];
    for (const load of needsInvoiceLoads) {
      try {
        const amount = computeLoadGross(load);
        const created = await client.entities.Invoice.create({
          invoice_number: nextInvoiceNumber(invoicesSoFar),
          load_id: load.id,
          load_number: load.load_number,
          customer_name: load.customer_name || 'Unknown',
          customer_email: load.customer_email || undefined,
          total_amount: amount,
          balance_due: amount,
          invoiced_date: new Date().toISOString().split('T')[0],
          status: 'Unpaid',
        });
        invoicesSoFar = [...invoicesSoFar, created];
        success++;
      } catch (e) {
        errors.push(`#${load.load_number}: ${e?.message || 'Unknown error'}`);
      }
    }
    setCreatingInvoiceFor(null);
    if (success > 0) toast.success(`${success} invoice(s) created.`);
    if (errors.length > 0) toast.error(`${errors.length} failed: ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '…' : ''}`);
    loadInvoices();
    loadNeedsInvoice();
  };

  // invoice_number is "INV-1047" -- a plain string sort would put
  // "INV-1000" before "INV-999" once digit counts differ, so this pulls
  // just the trailing number out for a real numeric comparison.
  const sortValue = (inv, key) => {
    if (key === 'invoice_number') return parseInt((inv.invoice_number || '').replace(/\D/g, ''), 10) || 0;
    return inv[key];
  };

  const toggleSort = (key) => {
    setSortState(prev => prev.key === key ? { key, dir: prev.dir * -1 } : { key, dir: 1 });
  };

  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      // Partial counts as still-owed -- grouped with Unpaid so a
      // partially-paid invoice never silently drops out of the
      // collections view. It still gets its own distinct status pill.
      if (statusFilter === 'Unpaid') return inv.status === 'Unpaid' || inv.status === 'Partial';
      if (statusFilter === 'Paid') return inv.status === 'Paid';
      const bucketFilter = BUCKET_FILTERS.find(b => b.label === statusFilter);
      if (bucketFilter) return agingBucket(invoiceAgingDays(inv)) === bucketFilter.key;
      return true; // All
    }).filter(inv => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (inv.customer_name || '').toLowerCase().includes(q) ||
             (inv.invoice_number || '').toLowerCase().includes(q) ||
             (inv.load_number || '').toLowerCase().includes(q);
    }).sort((a, b) => {
      const av = sortValue(a, sortState.key);
      const bv = sortValue(b, sortState.key);
      // Blanks always sink to the bottom regardless of direction (e.g.
      // sorting by Paid On with a mix of paid/unpaid invoices).
      if ((av == null || av === '') && (bv == null || bv === '')) return 0;
      if (av == null || av === '') return 1;
      if (bv == null || bv === '') return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sortState.dir * (av - bv);
      return sortState.dir * String(av).localeCompare(String(bv));
    });
  }, [invoices, statusFilter, search, sortState]);

  const totalBalanceDue = useMemo(() =>
    invoices.filter(i => i.status === 'Unpaid' || i.status === 'Partial').reduce((s, i) => s + (i.balance_due || 0), 0),
    [invoices]
  );

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(i => i.id)));
    }
  };

  const handleBatchPay = async () => {
    if (selected.size === 0) { toast.error('Select at least one invoice.'); return; }
    setBatchPaying(true);
    const today = new Date().toISOString().split('T')[0];
    await Promise.all([...selected].map(id =>
      client.entities.Invoice.update(id, { status: 'Paid', balance_due: 0, paid_date: today })
    ));
    toast.success(`${selected.size} invoice(s) marked as paid.`);
    setSelected(new Set());
    setBatchPaying(false);
    loadInvoices();
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    // Deleting the Invoice row alone doesn't undo the status flip
    // AddInvoiceModal made when it was created (Delivered -> Invoiced) --
    // without this, the load is left claiming "Invoiced" with no invoice
    // behind it: invisible to AdminPanel's InvoiceDropdown (which only
    // renders for status === 'Delivered'), yet not actually invoiced.
    // Only revert if the load is still sitting at 'Invoiced' -- if
    // something else already moved it on, leave it alone.
    if (inv.load_id) {
      try {
        const load = await client.entities.Load.get(inv.load_id);
        if (load && load.status === 'Invoiced') {
          await client.entities.Load.update(inv.load_id, { status: 'Delivered' });
        }
      } catch (e) {
        console.error('Failed to revert linked load status on invoice delete:', e);
      }
    }
    await client.entities.Invoice.delete(inv.id);
    toast.success('Invoice deleted.');
    loadInvoices();
    loadNeedsInvoice();
    onInvoiceDeleted?.();
  };


  const inputCls = "px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";

  const sortableTh = (label, key, align = 'left') => (
    <th
      onClick={() => toggleSort(key)}
      className={cn(
        "px-3 py-2.5 text-muted-foreground font-medium cursor-pointer hover:text-foreground select-none",
        align === 'right' ? 'text-right' : 'text-left'
      )}
    >
      <span className={cn("flex items-center gap-1", align === 'right' && 'justify-end')}>
        {label}
        {sortState.key === key
          ? (sortState.dir === 1 ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />)
          : <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </span>
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          {/* Status pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {['All', 'Unpaid', ...BUCKET_FILTERS.map(b => b.label), 'Paid'].map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={cn(
                  "text-xs font-semibold px-3 py-1 rounded-full border transition-colors",
                  statusFilter === f
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                )}
              >
                {f}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer, invoice #, or load #…"
              className={cn(inputCls, "pl-8 w-64")}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Total balance due */}
          <div className="glass-card px-4 py-2.5 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground leading-none">Total Balance Due</p>
              <p className="text-lg font-space font-bold text-primary leading-tight">${totalBalanceDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <Button
            onClick={handleBatchPay}
            disabled={selected.size === 0 || batchPaying}
            variant="outline"
            className="border-border text-sm"
          >
            {batchPaying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CreditCard className="w-4 h-4 mr-1" />}
            Batch Payment Received {selected.size > 0 && `(${selected.size})`}
          </Button>
          <Button onClick={() => setShowBatch(true)} variant="outline" className="border-border text-sm">
            <Layers className="w-4 h-4 mr-2" /> Batch Invoice
          </Button>
          <Button onClick={() => setShowAdd(true)} className="bg-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-2" /> Add Invoice
          </Button>
        </div>
      </div>

      {/* Needs Invoice -- loads dispatch released to Accounting (status
          flipped to Invoiced) that don't have an Invoice row behind them
          yet. The status flip alone was silently leaving these released
          loads invisible: not 'Delivered' anymore, but not actually
          invoiced either. */}
      {!needsInvoiceLoading && needsInvoiceLoads.length > 0 && (
        <div className="glass-card p-4 border border-amber-500/30 bg-amber-500/5 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <p className="text-sm font-semibold text-foreground">
                {needsInvoiceLoads.length} load{needsInvoiceLoads.length === 1 ? '' : 's'} released to Accounting with no invoice yet
              </p>
            </div>
            <Button
              onClick={handleCreateAllNeedsInvoice}
              disabled={creatingInvoiceFor !== null}
              className="bg-amber-500 text-black hover:bg-amber-400 text-sm"
            >
              {creatingInvoiceFor === 'bulk' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Create All ({needsInvoiceLoads.length})
            </Button>
          </div>
          <div className="divide-y divide-amber-500/20 max-h-56 overflow-y-auto">
            {needsInvoiceLoads.map(l => (
              <div key={l.id} className="flex items-center justify-between gap-3 py-2 text-xs">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-semibold text-foreground whitespace-nowrap">#{l.load_number}</span>
                  <span className="text-muted-foreground truncate">{l.customer_name || 'No customer'}</span>
                  <span className="text-muted-foreground whitespace-nowrap">${computeLoadGross(l).toLocaleString()}</span>
                </div>
                <button
                  onClick={() => handleCreateInvoiceForLoad(l)}
                  disabled={creatingInvoiceFor !== null}
                  className="px-2 py-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-400 font-medium hover:bg-amber-500/20 transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {creatingInvoiceFor === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create Invoice'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="glass-card p-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-2.5 w-8">
                    <button onClick={toggleAll} className="text-muted-foreground hover:text-foreground">
                      {selected.size === filtered.length && filtered.length > 0
                        ? <CheckSquare className="w-3.5 h-3.5" />
                        : <Square className="w-3.5 h-3.5" />}
                    </button>
                  </th>
                  {sortableTh('Invoice #', 'invoice_number')}
                  {sortableTh('Load #', 'load_number')}
                  {sortableTh('Customer', 'customer_name')}
                  {sortableTh('Total', 'total_amount', 'right')}
                  {sortableTh('Balance Due', 'balance_due', 'right')}
                  {sortableTh('Invoiced On', 'invoiced_date')}
                  {sortableTh('Paid On', 'paid_date')}
                  {sortableTh('Status', 'status')}
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(inv => {
                  const agingDays = invoiceAgingDays(inv);
                  return (
                    <tr
                      key={inv.id}
                      className={cn(selected.has(inv.id) && 'bg-primary/10', 'hover:bg-white/3 transition-colors')}
                    >
                      <td className="px-3 py-2.5">
                        <button onClick={() => toggleSelect(inv.id)} className="text-muted-foreground hover:text-foreground">
                          {selected.has(inv.id) ? <CheckSquare className="w-3.5 h-3.5 text-primary" /> : <Square className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 font-semibold">
                        <button onClick={() => setEditInvoice(inv)} className="text-blue-600 dark:text-blue-400 hover:underline">
                          {inv.invoice_number}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        {inv.load_number ? (
                          <button
                            onClick={() => openLoad(inv)}
                            disabled={openingLoadId === inv.id}
                            title="Open load — rate con, BOL, receipts"
                            className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                          >
                            #{inv.load_number}
                          </button>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-foreground max-w-[160px] truncate">{inv.customer_name}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-foreground">${(inv.total_amount || 0).toLocaleString()}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-foreground">
                        ${(inv.balance_due || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{inv.invoiced_date || '—'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{inv.paid_date || '—'}</td>
                      <td className="px-3 py-2.5">
                        <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border", STATUS_COLORS[inv.status] || STATUS_COLORS.Unpaid)}>
                          {inv.status}{agingDays != null && agingDays > 90 ? ' ⚠ 90+' : ''}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1 justify-end">
                          {(inv.status === 'Unpaid' || inv.status === 'Partial') && (
                            <button
                              onClick={() => setPayingInvoice(inv)}
                              className="px-2 py-1 rounded-lg bg-green-500/15 dark:bg-green-500/10 border border-green-500/40 dark:border-green-500/20 text-green-700 dark:text-green-400 text-[10px] font-semibold hover:bg-green-500/25 dark:hover:bg-green-500/20 transition-colors whitespace-nowrap flex items-center gap-1"
                            >
                              <CreditCard className="w-3 h-3" /> Payment Received
                            </button>
                          )}
                          <button
                            onClick={() => setEmailingInvoice(inv)}
                            title={inv.customer_email ? `Email to ${inv.customer_email}` : 'No email on file for this customer'}
                            className="px-2 py-1 rounded-lg text-muted-foreground text-[10px] hover:text-foreground hover:bg-white/10 transition-colors flex items-center gap-1"
                          >
                            <Mail className="w-3 h-3" /> Email
                          </button>
                          {(inv.status === 'Unpaid' || inv.status === 'Partial') && agingDays != null && agingDays > 30 && (
                            <button
                              onClick={() => setRemindingInvoice(inv)}
                              title={inv.customer_email ? `Send a payment reminder to ${inv.customer_email}` : 'No email on file for this customer'}
                              className="px-2 py-1 rounded-lg bg-amber-500/15 dark:bg-amber-500/10 border border-amber-500/40 dark:border-amber-500/20 text-amber-700 dark:text-amber-400 text-[10px] font-semibold hover:bg-amber-500/25 dark:hover:bg-amber-500/20 transition-colors whitespace-nowrap flex items-center gap-1"
                            >
                              <AlertTriangle className="w-3 h-3" /> Remind
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(inv)}
                            className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <p className="text-center text-muted-foreground py-10 text-sm">No invoices found.</p>
            )}
          </div>
        </div>
      )}

      {showAdd && (
        <AddInvoiceModal
          initialLoadId={prefillLoadId}
          onClose={() => { setShowAdd(false); onPrefillConsumed?.(); }}
          onCreated={() => { loadInvoices(); loadNeedsInvoice(); onPrefillConsumed?.(); onInvoiceCreated?.(); }}
        />
      )}
      {editInvoice && (
        <AddInvoiceModal
          invoice={editInvoice}
          onClose={() => setEditInvoice(null)}
          onCreated={loadInvoices}
        />
      )}
      {payingInvoice && <EnterPaymentModal invoice={payingInvoice} onClose={() => setPayingInvoice(null)} onPaid={loadInvoices} />}
      {emailingInvoice && (
        <EmailInvoiceModal
          invoice={emailingInvoice}
          onClose={() => setEmailingInvoice(null)}
          onSent={loadInvoices}
        />
      )}
      {remindingInvoice && (
        <EmailInvoiceModal
          invoice={remindingInvoice}
          mode="reminder"
          onClose={() => setRemindingInvoice(null)}
          onSent={loadInvoices}
        />
      )}
      {viewLoad && <LoadDetailModal load={viewLoad} onClose={() => setViewLoad(null)} onRefresh={loadInvoices} />}
      {showBatch && (
        <BatchInvoiceModal
          onClose={() => setShowBatch(false)}
          onCreated={() => { loadInvoices(); loadNeedsInvoice(); }}
        />
      )}
    </div>
  );
}